import { useTranslation } from "react-i18next";
import type { ProgramEntry } from "./types";

/**
 * Düğün programı — TÜM etkinlikler kronolojik sırada, eşit ağırlıkta.
 *
 * NEDEN BÖYLE (tasarım kararı, 2026-08-04):
 * Davetiyedeki QR herkese açık ve iki etkinlik farklı şehirlerde olabiliyor.
 * QR'ı okutan misafirin yalnızca BİR etkinliğe davetli olma ihtimali yüksek —
 * hatta diğer şehirle hiçbir bağı olmayabilir. Bu yüzden:
 *   • "asıl etkinlik" diye bir vurgu YOK; kronoloji açık,
 *   • her etkinlik KENDİ gün sayacını taşır (tek bir büyük geri sayım, davetli
 *     olmadığı etkinliğe bakan misafiri yanıltıyordu),
 *   • yer bilgisi her satırda görünür ki misafir kendi etkinliğini bulsun.
 * Sektör deseni de bu (WedSites/Joy/Zola "schedule" sayfaları).
 *
 * Tek etkinlikli düğünlerde bu bileşen HİÇ render edilmez — o düğünler bugünkü
 * büyük canlı geri sayımı korur (regresyon yok).
 *
 * Tema-nötr: renk `currentColor`'dan türer, zemin şeffaftır.
 */
export function WeddingProgram({
  entries,
  className = "",
}: {
  entries: ProgramEntry[];
  className?: string;
}) {
  const { t } = useTranslation();
  if (entries.length < 2) return null;

  function countdownText(e: ProgramEntry): string {
    if (e.daysUntil < 0) return t("wedding.program.past");
    if (e.daysUntil === 0) return t("wedding.program.today");
    if (e.daysUntil === 1) return t("wedding.program.tomorrow");
    return t("wedding.program.daysLeft", { count: e.daysUntil });
  }

  return (
    <div className={`w-full ${className}`}>
      <p className="mb-8 text-center text-[10px] font-medium tracking-[0.4em] uppercase opacity-40">
        {t("wedding.program.programTitle")}
      </p>

      {/*
        Ayraç sütun boyunca DEĞİL, ortada ve kısa: kenardan kenara bir kural
        iki etkinliği "ayrı sayfalar" gibi gösteriyordu (Onyx'te de aynı düzeltme).
      */}
      <div className="relative grid gap-10 sm:grid-cols-2 sm:gap-14">
        <div
          aria-hidden="true"
          data-testid="program-divider"
          className="pointer-events-none absolute left-1/2 top-1/2 hidden h-[60%] w-px -translate-x-1/2 -translate-y-1/2 bg-current/10 sm:block"
        />
        {entries.map((e, i) => (
          <div
            key={e.key}
            className={[
              "flex flex-col items-center gap-2 text-center",
              // Mobilde sütun ayracı yok — ikinciyi ince bir çizgi ayırır.
              i > 0 ? "border-t border-current/10 pt-10 sm:border-t-0 sm:pt-0" : "",
            ].join(" ")}
          >
            <span className="text-[10px] font-medium tracking-[0.35em] uppercase opacity-55">
              {e.name}
            </span>

            <p
              className="font-serif text-3xl font-light leading-none tracking-tight sm:text-4xl"
              style={{ fontFamily: "var(--font-serif, 'Playfair Display', serif)" }}
            >
              {e.dateLabel}
            </p>

            <p className="text-[11px] tracking-[0.2em] uppercase opacity-60">
              {[e.weekdayLabel, e.timeLabel].filter(Boolean).join(" · ")}
            </p>

            {e.location && (
              <p className="max-w-[22rem] text-xs leading-relaxed opacity-70">{e.location}</p>
            )}

            <span
              className={[
                "mt-1 text-[10px] font-medium tracking-[0.3em] uppercase",
                e.daysUntil < 0 ? "opacity-35" : "opacity-70",
              ].join(" ")}
            >
              {countdownText(e)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
